import { AIActionsButton, AIActionsButtonVariant } from 'components/Buttons/AIActionsButton';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall } from 'globalStyles';
import { useEffect, useRef } from 'react';
import NewTaskWizard, { INewTaskWizardProps, WizardStep } from './NewTaskWizard';
import { WizardAIResultBar, WizardAIWorking } from './NewTaskWizardAIParts';
import { useNewTaskWizardAI } from './useNewTaskWizardAI';

type INewTaskWizardWithAIProps = Omit<INewTaskWizardProps, 'aiChecklistActions' | 'renderAIResult'>;

// The new task wizard with its AI action: passes the AI state to the wizard's `aiChecklistActions` and
// `renderAIResult` props.
const NewTaskWizardWithAI = (props: INewTaskWizardWithAIProps) => {
  const ai = useNewTaskWizardAI({
    isOpen: props.isOpen,
    task: props.task,
    canUpdate: props.canUpdate,
    onUpdateTask: props.onUpdateTask,
  });

  //a result that appears below the fold is scrolled into view
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ai.bar) {
      resultRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [ai.bar]);

  const bindings = ai.getBindings();
  const aiChecklistActions =
    bindings.length > 0 ? <AIActionsButton variant={AIActionsButtonVariant.Action} bindings={bindings} /> : undefined;

  const renderResult = (step: WizardStep) => {
    const bar = ai.bar?.step === step ? ai.bar : undefined;

    if (!ai.isRunning && !bar) return null;

    return (
      <div ref={resultRef}>
        <IPStack tokens={globalStackTokensGapSmall}>
          {ai.isRunning && <WizardAIWorking />}
          {bar && <WizardAIResultBar lines={bar.lines} onUndo={ai.undo} onDismiss={ai.dismissBar} />}
        </IPStack>
      </div>
    );
  };

  return (
    <NewTaskWizard
      {...props}
      isActionPending={props.isActionPending || ai.isRunning}
      aiChecklistActions={aiChecklistActions}
      renderAIResult={renderResult}
    />
  );
};

export default NewTaskWizardWithAI;
